import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { googleLogout } from '@react-oauth/google';
import { setUnauthorizedHandler } from '../api/client';
import { AuthUser, fetchMe, logout as logoutRequest, signInWithGoogle } from '../api/auth';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;
  signIn: (googleIdToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  retry: () => void;
  // UX only — never a security boundary. The server enforces role checks.
  hasRole: (role: 'admin' | 'standard') => boolean;
  // Sign-in is a popup over the current page, not a route — any component
  // anywhere in the app (header widget, a gated page's fallback, the chat
  // attribution notice) can open it without navigating away.
  isLoginModalOpen: boolean;
  openLoginModal: () => void;
  closeLoginModal: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  const navigate = useNavigate();

  const openLoginModal = useCallback(() => setLoginModalOpen(true), []);
  const closeLoginModal = useCallback(() => setLoginModalOpen(false), []);

  const loadMe = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const me = await fetchMe();
      setUser(me);
      setStatus('authenticated');
    } catch (err: any) {
      const httpStatus = err?.response?.status;
      if (httpStatus === 401) {
        // Not signed in — the normal state for a first-time visitor, not an error.
        setUser(null);
        setStatus('unauthenticated');
      } else {
        // Network failure or 503 — a materially different situation from a clean 401.
        setUser(null);
        setError('Unable to reach the authentication service. Please try again.');
        setStatus('error');
      }
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      // The session lapsed (401 from an authenticated call). Surface the
      // sign-in popup in place rather than navigating away — there's no
      // dedicated /login page to bounce to anymore, and staying put means
      // whatever the user was doing (e.g. on /dashboard) resumes right
      // where it was once they re-auth.
      setUser(null);
      setStatus('unauthenticated');
      setLoginModalOpen(true);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const signIn = useCallback(async (googleIdToken: string) => {
    const me = await signInWithGoogle(googleIdToken);
    setUser(me);
    setStatus('authenticated');
    setLoginModalOpen(false);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await logoutRequest();
    } catch {
      // A user who clicks sign-out must never still see the dashboard,
      // regardless of whether the logout call itself succeeded.
    }
    googleLogout();
    setUser(null);
    setStatus('unauthenticated');
    // /dashboard requires auth and would immediately re-guard anyway; home
    // is always a valid destination regardless of where sign-out was clicked.
    navigate('/');
  }, [navigate]);

  const retry = useCallback(() => {
    loadMe();
  }, [loadMe]);

  const hasRole = useCallback((role: 'admin' | 'standard') => user?.role === role, [user]);

  const value: AuthContextValue = {
    status, user, error, signIn, signOut, retry, hasRole,
    isLoginModalOpen, openLoginModal, closeLoginModal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
